/**
 * S3 PUT via XMLHttpRequest.
 *
 * 为什么不是 fetch:浏览器 fetch 拿不到 upload progress,也没法在 presigned
 * PUT 上做细粒度中止(见 docs/plans/2026-08-25-comment-attachments-design.md §5.2)。
 * 只有 S3 PUT 这一步走 XHR,presign / commit 仍走 axios。
 */

export interface PutToS3Options {
  /** 字节级上传进度。只有 lengthComputable 的事件才回调。 */
  onProgress?: (loaded: number, total: number) => void
  /** 中止信号。abort 后 promise reject DOMException('Aborted','AbortError')。 */
  signal?: AbortSignal
}

export function putToS3(
  url: string,
  body: Blob,
  headers: Record<string, string>,
  opts: PutToS3Options = {},
): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    const abortError = () => new DOMException('Aborted', 'AbortError')

    if (opts.signal?.aborted) {
      reject(abortError())
      return
    }

    const xhr = new XMLHttpRequest()
    xhr.open('PUT', url, true)
    for (const [key, value] of Object.entries(headers)) {
      xhr.setRequestHeader(key, value)
    }

    const onAbortSignal = () => xhr.abort()
    const cleanup = () => opts.signal?.removeEventListener('abort', onAbortSignal)
    opts.signal?.addEventListener('abort', onAbortSignal)

    if (opts.onProgress && xhr.upload) {
      xhr.upload.onprogress = (event: ProgressEvent) => {
        if (event.lengthComputable) opts.onProgress?.(event.loaded, event.total)
      }
    }

    xhr.onload = () => {
      cleanup()
      if (xhr.status >= 200 && xhr.status < 300) resolve()
      else reject(new Error(`S3 upload failed: ${xhr.status} ${xhr.statusText}`))
    }
    xhr.onerror = () => {
      cleanup()
      reject(new Error('S3 upload failed: network error'))
    }
    xhr.onabort = () => {
      cleanup()
      reject(abortError())
    }

    xhr.send(body)
  })
}
